import React, { useState, useEffect, useMemo } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline, Box, Alert, Snackbar, CircularProgress, Typography } from '@mui/material';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import AppBar from './components/layout/AppBar';
import Dashboard from './components/Dashboard';
import EnhancedDashboard from './components/EnhancedDashboard';
import AlertsPage from './components/alerts/AlertsPage';
import EmailPageContainer from './components/email/EmailPageContainer';
import SettingsPage from './components/subscription/SettingsPage';
import WebhookActivityPage from './components/webhooks/WebhookActivityPage';
import NotificationCenter from './components/notifications/NotificationCenter';
import SupportPage from './components/support/SupportPage';
import AdminRouter from './components/Admin/AdminRouter';

// Import contexts
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { PreferencesProvider, usePreferences } from './contexts/PreferencesContext';
import { DashboardProvider } from './contexts/DashboardContext';
import { WebSocketProvider } from './contexts/WebSocketContext';
import { AdminProvider } from './contexts/AdminContext';
import { HelpProvider } from './contexts/HelpContext';

import { apiService } from './services/apiService';
import { getAppTheme } from './theme/theme';
import { useSystemStatus } from './hooks/useApi';
import { EmergencyControls } from './types/dashboard';
import LoginForm from './components/auth/LoginForm';
import ForgotPasswordForm from './components/auth/ForgotPasswordForm';
import ResetPasswordForm from './components/auth/ResetPasswordForm';
import VerifyEmailForm from './components/auth/VerifyEmailForm';
import ProtectedRoute from './components/auth/ProtectedRoute';
import LimitedModeRoute from './components/limited-mode/LimitedModeRoute';
import LandingPage from './components/LandingPage';
import ErrorBoundary from './components/ErrorBoundary';

const AppContent: React.FC = () => {
  const { user, tenant, token, logout, isAuthenticated, isInitializing: authInitializing, updateUser } = useAuth();
  const { preferences, updatePreference } = usePreferences();

  // Persist Personal Information edits (name/email) to the backend and refresh
  // the in-memory user so the page and notifications use the new values.
  const handleUserInfoUpdate = async (info: Partial<{ name: string; email: string }>) => {
    const payload: Record<string, string> = {};
    if (typeof info.name === 'string') payload.name = info.name;
    if (typeof info.email === 'string') payload.email = info.email;
    if (Object.keys(payload).length === 0) return;
    const res = await apiService.put('/api/v1/users/profile', payload, {
      headers: { 'Content-Type': 'application/json' },
    });
    if (res.success) {
      updateUser(payload);
    } else {
      throw new Error(res.error || 'Failed to update profile');
    }
  };

  // Resolve theme: preferences.theme (light/dark/auto) -> isDarkMode
  const isDarkMode = (() => {
    if (preferences.theme === 'light') return false;
    if (preferences.theme === 'dark') return true;
    return typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches;
  })();

  const handleThemeToggle = () => {
    updatePreference('theme', isDarkMode ? 'light' : 'dark');
  };

  // Apply font size globally. Our typography is rem-based, so scaling the root
  // <html> font size scales the whole UI proportionally.
  useEffect(() => {
    const rootPx =
      preferences.fontSize === 'small' ? 14 : preferences.fontSize === 'large' ? 18 : 16;
    document.documentElement.style.fontSize = `${rootPx}px`;
    return () => {
      document.documentElement.style.fontSize = '';
    };
  }, [preferences.fontSize]);

  const [notification, setNotification] = useState<{ message: string; severity: 'success' | 'error' | 'warning' | 'info' } | null>(null);
  const [emergencyControls, setEmergencyControls] = useState<EmergencyControls>({
    isStopped: false,
    canReactivate: true,
  });

  // Low confidence alerts count (mock for now)
  const [lowConfidenceCount] = useState(0);

  const handleEmergencyStop = () => {
    setEmergencyControls(prev => ({ ...prev, isStopped: true, canReactivate: false }));
    setNotification({
      message: 'Emergency stop activated. System is now stopped.',
      severity: 'warning'
    });
  };

  const handleEmergencyReactivate = () => {
    setEmergencyControls(prev => ({ ...prev, isStopped: false, canReactivate: true }));
    setNotification({
      message: 'System reactivated successfully.',
      severity: 'success'
    });
  };

  const handleCloseNotification = () => {
    setNotification(null);
  };

  const currentTheme = useMemo(
    () => getAppTheme(isDarkMode, { compactMode: preferences.compactMode }),
    [isDarkMode, preferences.compactMode],
  );

  // System status hook - always call hooks at the top level
  const { data: systemStatus, error: statusError, loading: statusLoading } = useSystemStatus();

  return (
    <ThemeProvider theme={currentTheme}>
      <CssBaseline />
      <Router>
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
          {isAuthenticated && user && tenant && (
            <AppBar
              systemStatus={systemStatus || { status: 'unknown', lastUpdate: new Date().toISOString(), uptime: 0, message: statusLoading ? 'Loading...' : 'Offline' }}
              emergencyControls={emergencyControls}
              onEmergencyStop={handleEmergencyStop}
              onEmergencyReactivate={handleEmergencyReactivate}
              onThemeToggle={handleThemeToggle}
              onLogout={logout}
              isDarkMode={isDarkMode}
              lowConfidenceCount={lowConfidenceCount}
            />
          )}
          
          {/* Error notification for API errors */}
          {statusError && (
            <Alert 
              severity="error" 
              onClose={() => setNotification(null)}
              sx={{ m: 2 }}
            >
              Failed to connect to backend: {statusError}
            </Alert>
          )}
          
          <Box component="main" sx={{ flexGrow: 1, p: 3 }} data-app="branch-router" data-auth-ready={!authInitializing}>
            {authInitializing ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', gap: 2 }}>
                <CircularProgress />
                <Typography variant="body2" color="text.secondary">Checking session…</Typography>
              </Box>
            ) : isAuthenticated && user && tenant ? (
              <HelpProvider>
                  <WebSocketProvider 
                    tenantId={tenant.id} 
                    userId={user.id} 
                    token={token || ''}
                onMessage={(message) => {
                  console.log('WebSocket message received:', message);
                }}
                onConnect={() => {
                  console.log('WebSocket connected');
                }}
                onDisconnect={() => {
                  console.log('WebSocket disconnected');
                }}
                onError={(error) => {
                  console.error('WebSocket error:', error);
                }}
              >
                <NotificationCenter tenantId={tenant.id} userId={user.id} token={token || ''} />
                <Routes>
                    <Route path="/forgot-password" element={<ForgotPasswordForm />} />
                    <Route path="/reset-password" element={<ResetPasswordForm />} />
                    <Route path="/verify-email" element={<VerifyEmailForm />} />
                    <Route path="/" element={
                      <ProtectedRoute>
                        <LimitedModeRoute>
                          <LandingPage user={user} tenant={tenant} />
                        </LimitedModeRoute>
                      </ProtectedRoute>
                    } />
                    <Route path="/dashboard" element={
                      <ProtectedRoute>
                        <LimitedModeRoute>
                          <DashboardProvider tenantId={tenant.id} userId={user.id} user={user} tenant={tenant}>
                            <EnhancedDashboard 
                              tenantId={tenant.id.toString()}
                              userId={user.id}
                              user={user} 
                              tenant={tenant} 
                            />
                          </DashboardProvider>
                        </LimitedModeRoute>
                      </ProtectedRoute>
                    } />
                    <Route path="/legacy" element={
                      <ProtectedRoute>
                        <Dashboard />
                      </ProtectedRoute>
                    } />
                    <Route path="/alerts" element={
                      <ProtectedRoute>
                        <LimitedModeRoute restricted>
                          <AlertsPage />
                        </LimitedModeRoute>
                      </ProtectedRoute>
                    } />
                    <Route path="/email" element={
                      <ProtectedRoute>
                        <LimitedModeRoute restricted>
                          <DashboardProvider tenantId={tenant.id} userId={user.id} user={user} tenant={tenant}>
                            <EmailPageContainer />
                          </DashboardProvider>
                        </LimitedModeRoute>
                      </ProtectedRoute>
                    } />
                    <Route path="/history" element={<Navigate to="/dashboard" replace />} />
                    <Route path="/settings" element={
                      <ProtectedRoute>
                        <SettingsPage
                          user={user}
                          onPlanChange={() => {}}
                          onPaymentMethodUpdate={() => {}}
                          onUserInfoUpdate={handleUserInfoUpdate}
                        />
                      </ProtectedRoute>
                    } />
                    <Route path="/webhook-activity" element={
                      <ProtectedRoute>
                        <LimitedModeRoute restricted>
                          <WebhookActivityPage />
                        </LimitedModeRoute>
                      </ProtectedRoute>
                    } />
                    <Route path="/support" element={
                      <ProtectedRoute>
                        <SupportPage />
                      </ProtectedRoute>
                    } />
                    <Route path="/tutorial" element={<Navigate to="/" replace />} />
                    <Route path="/admin/*" element={
                      <ProtectedRoute>
                        <AdminProvider>
                          <AdminRouter onLogout={logout} />
                        </AdminProvider>
                      </ProtectedRoute>
                    } />
                </Routes>
              </WebSocketProvider>
          </HelpProvider>
        ) : (
              <Routes>
                <Route path="/login" element={<LoginForm />} />
                <Route path="/forgot-password" element={<ForgotPasswordForm />} />
                <Route path="/reset-password" element={<ResetPasswordForm />} />
                <Route path="/verify-email" element={<VerifyEmailForm />} />
                <Route path="*" element={<LoginForm />} />
              </Routes>
            )}
          </Box>
          
          {/* Notification Snackbar */}
          <Snackbar
            open={!!notification}
            autoHideDuration={6000}
            onClose={handleCloseNotification}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          >
            <Alert 
              onClose={handleCloseNotification} 
              severity={notification?.severity || 'info'}
              sx={{ width: '100%' }}
            >
              {notification?.message}
            </Alert>
          </Snackbar>
        </Box>
      </Router>
    </ThemeProvider>
  );
};

const APP_DOCUMENT_TITLE = 'Branch Router Automated Alert System';

function App() {
  useEffect(() => {
    document.title = APP_DOCUMENT_TITLE;
  }, []);

  return (
    <ErrorBoundary>
      <AuthProvider>
        <PreferencesProvider>
          <AppContent />
        </PreferencesProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;